/**
 * Les catalogues de formes d'un éditeur (Toolbox::ShapeCatalog.folders_for_editor), servis en
 * JSON statiques sous des URLs empreintées (Toolbox::ShapeCatalogsController). L'éditeur naît
 * sans les attendre — un document se suffit (clips inlinés, paths sérialisés) : chaque catalogue
 * rejoint le registre de la lib à son arrivée, concaténé dans l'ordre donné (global d'abord — les
 * clipShape du stock s'y résolvent), sans doublon d'id. Un catalogue déjà demandé sur la page
 * n'est pas redemandé ; un catalogue en échec manque, l'éditeur vit sans lui.
 */
import { getCatalogShape, registerShapes, type CatalogShapeInput } from "@frabr/fabric-editor";

const pending = new Map<string, Promise<CatalogShapeInput[]>>();
let settled: Promise<unknown> = Promise.resolve();

/**
 * Lance le chargement des catalogues ; `onRegistered` suit chaque arrivée, une fois le registre
 * à jour. La promesse rendue se règle quand tous sont arrivés ou ont échoué.
 */
export function streamShapeCatalogs(
  urls: string[],
  onRegistered: () => void = () => {},
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  const arrived: CatalogShapeInput[][] = urls.map(() => []);
  const all = Promise.allSettled(urls.map((url, index) =>
    load(url, fetchFn).then((shapes) => {
      arrived[index] = shapes;
      registerShapes(dedupe(arrived.flat()));
      onRegistered();
    }, (error) => console.error(error)),
  )).then(() => undefined);
  settled = all;
  return all;
}

/** Tous les catalogues demandés sont arrivés (ou ont échoué) : de quoi créer une forme. */
export function shapeCatalogsSettled(): Promise<unknown> {
  return settled;
}

/**
 * Le document porte un clip par id seul (stock legacy, sans clipData inliné) que le registre
 * sait désormais résoudre : affiché en rectangle jusque-là, il demande un remontage.
 */
export function hasResolvableLegacyClip(layers: unknown): boolean {
  if (Array.isArray(layers)) return layers.some(hasResolvableLegacyClip);
  if (!layers || typeof layers !== "object") return false;

  const { clipShape, clipData } = layers as { clipShape?: unknown; clipData?: unknown };
  if (typeof clipShape === "string" && !clipData && getCatalogShape(clipShape)) return true;
  return Object.values(layers).some((value) => typeof value === "object" && hasResolvableLegacyClip(value));
}

function dedupe(shapes: CatalogShapeInput[]): CatalogShapeInput[] {
  const seen = new Set<string>();
  return shapes.filter((shape) => !seen.has(shape.id) && seen.add(shape.id));
}

function load(url: string, fetchFn: typeof fetch): Promise<CatalogShapeInput[]> {
  let promise = pending.get(url);
  if (!promise) {
    promise = fetchFn(url).then((response) => {
      if (!response.ok) throw new Error(`catalogue de formes ${url} : HTTP ${response.status}`);
      return response.json() as Promise<CatalogShapeInput[]>;
    });
    pending.set(url, promise);
    // Un échec ne se mémorise pas : la prochaine init réessaie.
    promise.catch(() => pending.delete(url));
  }
  return promise;
}
